import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import { marked, parse } from "marked";
import { generateAuditedClient } from "../../audit/auditedClient";
import { publishReport, unpublishReport } from "../../graphql/mutations";
import { getPublishedReportsSummary } from "../../graphql/queries";
import type {
  GetPublishedReportsInput,
  PublishedReportSource,
  PublishedReportSummary,
  PublishReportInput,
  UnpublishReportInput,
} from "../../models/API";
import {
  allowedVisibilities,
  buildProvenance,
  defaultTitleFor,
  publishedReportDeepLink,
  ReportVisibility,
  TITLE_MAX_LENGTH,
  validateTitle,
  visibilityFlags,
  visibilityOf,
  VISIBILITY_LABELS,
} from "./publishedReportModel";

marked.setOptions({ breaks: true, gfm: true });

/** The completed answer being published, as the chat holds it. */
export interface PublishTarget {
  /** Source turn id ({thread}#{epoch}#{uuid}). */
  conversationId: string;
  question: string;
  /** Marker-stripped answer markdown exactly as the chat rendered it. */
  markdown: string;
  toolCalls?: any[];
  sources?: any[];
  requestScope?: PublishedReportSource["request_scope"];
}

interface PublishReportDialogProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  teamId: number;
  teamName: string;
  effectiveRole: string;
  target: PublishTarget | null;
  /** Fired after a successful publish, edit or unpublish. */
  onChanged?: (report: PublishedReportSummary | null) => void;
}

type Mode = "loading" | "create" | "existing" | "published";

// First AppSync error message, else the fallback (same helper as ShareLinkDialog).
function errorMessageOf(err: any, fallback: string): string {
  const message = err?.errors?.[0]?.message ?? err?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

const PublishReportDialog: React.FC<PublishReportDialogProps> = ({
  open,
  onClose,
  userId,
  teamId,
  teamName,
  effectiveRole,
  target,
  onChanged,
}) => {
  const [mode, setMode] = useState<Mode>("loading");
  const [existing, setExisting] = useState<PublishedReportSummary | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [visibility, setVisibility] = useState<ReportVisibility>("private");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const client = generateAuditedClient();
  const allowed = allowedVisibilities(effectiveRole, teamId);
  const titleError = validateTitle(title);

  // Reset and pre-check "already published?" each time the dialog opens
  // (Req 1.4, 1.5). The lookup is own-rows-only across teams, so a report the
  // user published under another team is found too.
  useEffect(() => {
    if (!open || !target) return;
    let cancelled = false;
    setMode("loading");
    setError(null);
    setExisting(null);
    setTitle(defaultTitleFor(target.question));
    setDescription("");
    setVisibility("private");
    async function precheck() {
      try {
        const input: GetPublishedReportsInput = {
          user_id: userId,
          team_id: teamId,
          conversation_id: target!.conversationId,
        };
        const result = await client.graphql({
          query: getPublishedReportsSummary,
          variables: { input },
        });
        if (cancelled) return;
        const found = (result.data.getPublishedReports ?? [])[0] ?? null;
        if (found) {
          setExisting(found);
          setTitle(found.title);
          setDescription(found.description ?? "");
          setVisibility(visibilityOf(found));
          setMode("existing");
        } else {
          setMode("create");
        }
      } catch (err) {
        console.error("[PublishReportDialog] pre-check failed:", err);
        if (!cancelled) {
          // Publishing is still possible; the server enforces uniqueness.
          setMode("create");
        }
      }
    }
    precheck();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.conversationId]);

  const handlePublish = async () => {
    if (!target || titleError || busy) return;
    setBusy(true);
    setError(null);
    try {
      const input: PublishReportInput = {
        team_id: teamId,
        user_id: userId,
        title: title.trim(),
        description: description.trim() || null,
        ...visibilityFlags(visibility),
        conversation_id: target.conversationId,
        question: target.question,
        content: target.markdown.trimEnd(),
        source: JSON.stringify(
          buildProvenance({
            requestScope: target.requestScope,
            toolCalls: target.toolCalls,
            sources: target.sources,
          })
        ),
      };
      const result = await client.graphql({
        query: publishReport,
        variables: { input },
      });
      const report = result.data.publishReport;
      if (!report) throw new Error("No report returned");
      setExisting(report);
      setMode("published");
      onChanged?.(report);
    } catch (err) {
      console.error("[PublishReportDialog] publish failed:", err);
      setError(
        errorMessageOf(err, "Could not publish this answer. Please try again.")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!existing || titleError || busy) return;
    setBusy(true);
    setError(null);
    try {
      const input: PublishReportInput = {
        id: existing.id,
        team_id: existing.team_id,
        user_id: userId,
        title: title.trim(),
        description: description.trim() || null,
        ...visibilityFlags(visibility),
      };
      const result = await client.graphql({
        query: publishReport,
        variables: { input },
      });
      const report = result.data.publishReport;
      if (!report) throw new Error("No report returned");
      setExisting(report);
      setSnack("Report updated");
      onChanged?.(report);
    } catch (err) {
      console.error("[PublishReportDialog] edit failed:", err);
      setError(
        errorMessageOf(err, "Could not update this report. Please try again.")
      );
    } finally {
      setBusy(false);
    }
  };

  const handleUnpublish = async () => {
    if (!existing || busy) return;
    setBusy(true);
    setError(null);
    try {
      const input: UnpublishReportInput = { id: existing.id, user_id: userId };
      const result = await client.graphql({
        query: unpublishReport,
        variables: { input },
      });
      if (!result.data.unpublishReport) throw new Error("No report returned");
      onChanged?.(null);
      setSnack("Report unpublished");
      // The answer can be published again now.
      setExisting(null);
      setTitle(defaultTitleFor(target?.question ?? ""));
      setDescription("");
      setVisibility("private");
      setMode("create");
    } catch (err) {
      console.error("[PublishReportDialog] unpublish failed:", err);
      setError(
        errorMessageOf(
          err,
          "Could not unpublish this report. Please try again."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const deepLink = existing ? publishedReportDeepLink(existing) : "";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(deepLink);
      setSnack("Link copied to clipboard");
    } catch (err) {
      console.error("[PublishReportDialog] clipboard copy failed:", err);
      setSnack("Could not copy the link. Please copy it manually.");
    }
  };

  const handleOpen = () => {
    window.location.assign(deepLink);
  };

  const visibilityField = (
    <FormControl component="fieldset">
      <FormLabel
        component="legend"
        sx={{ fontWeight: 500, fontSize: "0.875rem" }}
      >
        Visibility
      </FormLabel>
      <RadioGroup
        value={visibility}
        onChange={(e) => setVisibility(e.target.value as ReportVisibility)}
        aria-label="visibility"
      >
        {(["private", "public", "global"] as ReportVisibility[]).map((v) => {
          const enabled =
            mode === "existing" && existing
              ? // Editing: the rules apply to the report's own team.
                allowedVisibilities(effectiveRole, existing.team_id).includes(v)
              : allowed.includes(v);
          const control = (
            <FormControlLabel
              key={v}
              value={v}
              control={<Radio size="small" disabled={!enabled} />}
              label={VISIBILITY_LABELS[v]}
              disabled={!enabled}
            />
          );
          if (enabled) return control;
          const why =
            v === "global"
              ? "Only FINN team operators/admins can publish global reports"
              : "Only operators/admins can publish to the team";
          return (
            <Tooltip key={v} title={why} placement="right">
              <span>{control}</span>
            </Tooltip>
          );
        })}
      </RadioGroup>
    </FormControl>
  );

  const editableFields = (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        autoFocus
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        required
        error={title.length > 0 && !!titleError}
        helperText={
          title.length > 0 && titleError
            ? titleError
            : `${title.trim().length}/${TITLE_MAX_LENGTH} characters`
        }
        inputProps={{ maxLength: TITLE_MAX_LENGTH + 20 }}
      />
      <TextField
        label="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        minRows={2}
      />
      {visibilityField}
      <Typography variant="body2" color="text.secondary">
        Team:{" "}
        <strong>
          {mode === "existing" && existing
            ? existing.team_name ?? teamName
            : teamName}
        </strong>
      </Typography>
    </Stack>
  );

  const preview = target && (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ mt: 2, "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Preview answer
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, whiteSpace: "pre-wrap", mb: 1 }}
        >
          {target.question}
        </Typography>
        <Box
          sx={{ maxHeight: 320, overflowY: "auto", fontSize: "0.9rem" }}
          dangerouslySetInnerHTML={{
            __html: (parse(target.markdown) as string).replace(/>\s+</g, "><"),
          }}
        />
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PublishOutlinedIcon fontSize="small" />
          <span>
            {mode === "existing"
              ? "Already published"
              : mode === "published"
              ? "Report published"
              : "Publish answer as a report"}
          </span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {mode === "loading" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <span>Checking this answer…</span>
          </Box>
        )}
        {mode === "create" && (
          <>
            <Typography variant="body2" color="text.secondary">
              Publishing stores a snapshot of this answer as a report in the
              Dashboard section, where the people you choose can find it without
              asking FINN again.
            </Typography>
            {editableFields}
            {preview}
          </>
        )}
        {mode === "existing" && existing && (
          <>
            <Alert severity="info" sx={{ mb: 1 }}>
              This answer is already published as{" "}
              <strong>{existing.title}</strong> (
              {VISIBILITY_LABELS[visibilityOf(existing)]
                .split(" (")[0]
                .toLowerCase()}
              , team {existing.team_name ?? existing.team_id}). You can update
              it or unpublish it here.
            </Alert>
            {editableFields}
          </>
        )}
        {mode === "published" && existing && (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>{existing.title}</strong> is now published to{" "}
              {VISIBILITY_LABELS[visibilityOf(existing)]
                .split(" (")[0]
                .toLowerCase()}{" "}
              visibility in team {existing.team_name ?? teamName}.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Report link"
                value={deepLink}
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Tooltip title="Copy link">
                <IconButton
                  onClick={handleCopyLink}
                  color="primary"
                  aria-label="Copy report link"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {mode === "create" && (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePublish}
              disabled={busy || !!titleError}
              startIcon={
                busy ? <CircularProgress size={16} /> : <PublishOutlinedIcon />
              }
            >
              Publish
            </Button>
          </>
        )}
        {mode === "existing" && (
          <>
            <Button onClick={handleUnpublish} color="error" disabled={busy}>
              Unpublish
            </Button>
            <Button
              onClick={handleOpen}
              startIcon={<OpenInNewIcon />}
              disabled={busy}
            >
              Open report
            </Button>
            <Button onClick={onClose} disabled={busy}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={busy || !!titleError}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
            >
              Save changes
            </Button>
          </>
        )}
        {mode === "published" && (
          <>
            <Button onClick={handleOpen} startIcon={<OpenInNewIcon />}>
              Open report
            </Button>
            <Button onClick={onClose} variant="contained">
              Done
            </Button>
          </>
        )}
        {mode === "loading" && <Button onClick={onClose}>Cancel</Button>}
      </DialogActions>
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={snack ?? ""}
      />
    </Dialog>
  );
};

export default PublishReportDialog;
